import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { AuthContext } from '../../context/auth-context'
import { getAuth, sendPasswordResetEmail, ActionCodeSettings } from 'firebase/auth'
import { Button } from '@/components/button/Button'
import { kPaxtonAppBaseDomain } from '@/constants/constants-links'
import * as Sentry from '@sentry/react'

type FormInputs = {
  email: string
}

type SendPasswordResetFormProps = {
  goToSignIn?: () => void
}

export default function SendPasswordResetForm(props: SendPasswordResetFormProps) {
  const { goToSignIn } = props
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const authContext = useContext(AuthContext)
  const user = authContext?.user

  // Redirect if the user is not null
  useEffect(() => {
    if (user != null && !user.isAnonymous && goToSignIn != null) {
      goToSignIn()
    }
  }, [goToSignIn, user])

  // Form Hook
  const { register, handleSubmit } = useForm<FormInputs>({
    defaultValues: {
      email: '',
    },
  })

  // Display generic message, prevent User Enumeration
  function DisplayMessage() {
    if (isFormSubmitted) {
      return (
        <div>
          <p className="text-center text-brand-neutral-950 text-sm">
            If this email is associated with an account, you'll receive a password reset link shortly. Don't forget to check your spam folder. For SSO users,
            use your usual login method.
          </p>
        </div>
      )
    } else {
      return null
    }
  }

  // Execute the password reset flow
  async function executePasswordResetFlow(data: FormInputs) {
    setLoading(true)
    try {
      const auth = getAuth()
      const actionCodeSettings: ActionCodeSettings = {
        url: `${kPaxtonAppBaseDomain()}/dashboard`,
      }

      await sendPasswordResetEmail(auth, data.email, actionCodeSettings)
    } catch (e) {
      console.warn(e)
      Sentry.captureException(e, {
        extra: {
          email: data.email,
        },
      })
    } finally {
      setIsFormSubmitted(true) // Set form as "complete" after sending the email
      setLoading(false)
    }
  }

  return (
    <>
      <h2 className="mb-2 text-center text-2xl font-bold tracking-tight text-brand-neutral-900">Password Reset</h2>
      <p className="text-center text-sm text-brand-neutral-600 mb-2">You will receive an email with a link to reset your password.</p>
      <form className="space-y-6" method="POST" onSubmit={handleSubmit((data) => executePasswordResetFlow(data))}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium leading-6 text-brand-neutral-900">
            Email address
          </label>
          <div className="">
            <input
              id="email-address"
              {...register('email', { required: true })}
              type="email"
              autoComplete="email"
              required
              className="block w-full rounded-md border-0 py-1.5 text-brand-neutral-900 shadow-xs ring-1 ring-inset ring-brand-neutral-300 pl-3 focus:ring-2 focus:ring-inset focus:ring-brand-500 sm:text-sm sm:leading-6"
              placeholder="Email address"
            />
          </div>
        </div>

        <DisplayMessage />

        <div>
          <Button type="submit" fullWidth disabled={isFormSubmitted} loading={loading}>
            {isFormSubmitted ? 'Email Sent' : 'Submit'}
          </Button>

          {goToSignIn && (
            <Button type="button" variant="plain" color="primary" fullWidth className="mt-4" onClick={() => goToSignIn()}>
              Back to Sign In
            </Button>
          )}
        </div>
      </form>
    </>
  )
}
